import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import logo from "../../assets/logo.png";

interface HeaderProps {
  theme: "light" | "dark";
  setTheme: (theme: "light" | "dark") => void;
  logout: () => void;
}

interface ValuationUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  profileImageUrl: string | null;
  role: string;
}

export default function Header({ theme, setTheme, logout }: HeaderProps) {
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [user, setUser] = useState<ValuationUser | null>(null);

  const location = useLocation();
  const pathname = location.pathname;
  const profileRef = useRef<HTMLDivElement>(null);

  /* ================= THEME SYNC ================= */
  useEffect(() => {
    const root = document.documentElement;

    if (theme === "dark") {
      root.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      root.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [theme]);

  /* ================= LOAD USER ================= */
  useEffect(() => {
    const storedUser = localStorage.getItem("valuation_user");
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error("Failed to parse valuation_user", error);
      }
    }
  }, []);

  /* ================= ACTIVE LINK ================= */
  const isActive = (href: string) => {
    if (href === "/institutional-client-dashboard") {
      return pathname === href;
    }
    return pathname.startsWith(href);
  };

  /* ================= OUTSIDE CLICK ================= */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileRef.current &&
        !profileRef.current.contains(event.target as Node)
      ) {
        setProfileMenuOpen(false);
      }
    };

    if (profileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [profileMenuOpen]);

  return (
    <header className="z-100 border-b border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-900 transition-colors">
      <nav>
        <div className="max-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">

            {/* LOGO */}
            <Link
              to="/institutional-client-dashboard"
              className="flex items-center"
            >
              <img src={logo} alt="Valuatify Logo" className="h-12 rounded-lg" />
              <span className="ml-3 text-sm font-bold text-gray-700 dark:text-gray-200">
                Real-time Property Valuation
              </span>
            </Link>

            {/* RIGHT SIDE */}
            <div className="flex items-center gap-3">

              {/* THEME TOGGLE */}
              <button
                className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center transition"
                onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                aria-label="Toggle theme"
              >
                {theme === "dark" ? "☀️" : "🌙"}
              </button>

              {/* PROFILE MENU */}
              <div className="relative" ref={profileRef}>
                <button
                  onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                  className="flex items-center text-sm rounded-md px-3 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
                >
                  {user?.profileImageUrl ? (
                    <img
                      src={user.profileImageUrl}
                      alt="Profile"
                      className="w-6 h-6 rounded-full mr-2"
                    />
                  ) : (
                    <span className="mr-2">👤</span>
                  )}
                  {user ? `${user.firstName} ${user.lastName}` : "Account"}
                  <span className="ml-1">▼</span>
                </button>

                {profileMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-50">
                    {["Profile Settings", "Account Security", "Preferences"].map(
                      (item) => (
                        <div
                          key={item}
                          className="px-4 py-2 text-sm cursor-pointer text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          {item}
                        </div>
                      )
                    )}

                    <div className="border-t border-gray-200 dark:border-gray-700 my-1" />

                    <div
                      onClick={() => {
                        logout();
                        window.location.href = "/login";
                      }}
                      className="px-4 py-2 text-sm cursor-pointer text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      Logout
                    </div>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </nav>
    </header>
  );
}
